/**
 * Weather coloring pages — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_coloring_themes`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const weatherColoringPagesPack: ToilePack = {
    slug: 'weather-coloring-pages',
    category: 'coloring',
    name: 'Weather coloring pages',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'chart', label: 'Weather chart', subject: 'a chart of six weather symbols in panels, sun, cloud, rain, snow, wind and storm, named "Sunny", "Cloudy", "Rainy", "Snowy", "Windy", "Stormy"'},
        {key: 'rain', label: 'Rainy day', subject: 'an open umbrella under a raincloud, puddles with splashes, rubber boots and a snail on the path'},
        {key: 'seasons', label: 'Seasons weather', subject: 'a page in four panels, summer sun over a beach, autumn wind blowing leaves, winter snowflakes on a snowman and spring showers over tulips'},
    ],
    styles: [
        {key: 'bold', label: 'Big and simple', style: 'thick black outline, big plain shapes, made for preschoolers', palette: 'black ink only', framing: 'The page centered with a wide white margin'},
        {key: 'cheerful', label: 'Cheerful faces', style: 'medium black outline, smiling faces on the sun, clouds and raindrops', palette: 'black ink only', framing: 'The page centered with a white margin'},
        {key: 'detailed', label: 'Detailed', style: 'thin black line, textured clouds, lightning, drifting snowflakes and wind swirls for older children', palette: 'black ink only', framing: 'The page filling the sheet with a thin white margin'},
    ],
    directions: ['rainbow', 'tornado', 'night sky'],
    directionHint: 'try: rainbow',
    seo: {
        title: 'Weather coloring pages: chart, rainy day, seasons',
        metaDescription: "Nine weather coloring pages: a labelled weather chart, a rainy-day scene and the four seasons' weather, in three styles.",
        h1: 'Weather coloring pages: weather chart, rainy day, seasons weather',
        tagline: 'A labelled chart, a rainy scene and the weather of each season, to color.',
        intro: 'Weather coloring pages for classroom weather lessons and morning circle time, and for children who like storms and snow. The nine pages show a labelled weather chart, a rainy-day scene and a page divided into the four seasons. The styles are big and simple, cheerful faces and detailed. Type a different weather, such as a rainbow or a tornado, and the same style carries it.',
        useCases: [
            'a daily weather chart for a classroom',
            'a morning circle activity for preschoolers',
            'a seasons lesson for older children',
            'a rainy-day activity at home',
        ],
        faq: [
            {q: 'What is on the seasons page?', a: 'A page divided into the four seasons, each with its own weather to color.'},
            {q: 'What is the difference between the styles?', a: 'Big and simple uses thick outlines and plain shapes for preschoolers. Cheerful faces adds smiles to the sun, clouds and raindrops, and the detailed style adds textured clouds, lightning, snowflakes and wind swirls for older children.'},
            {q: 'Can I ask for a different weather?', a: 'Yes. A weather you type, such as a rainbow, a tornado or a night sky, is a request; the page keeps the same style but may not match it exactly.'},
            {q: 'Are the pages in color?', a: 'No. Each page is a black line drawing on white paper, ready to print and color.'},
        ],
    },
}
